import { useCallback, useEffect, useRef, type MouseEvent, type PointerEvent, type RefObject } from "react";
import { SWIPE, decideSwipe, shouldDismiss, velocityOf, type SwipeDecision, type SwipeSample } from "./swipeGesture";

/** Fields a finger may be selecting text or moving a caret in. */
const TEXT_ENTRY_SELECTOR = 'input, textarea, select, [contenteditable]:not([contenteditable="false"])';

/** Outlasts the slide shut: a transition that never starts reports no end. */
const CLOSE_FALLBACK_MS = 200;

/** A drag well past the tap slop ends in no click, so the flag cannot wait for one. */
const SWALLOW_CLICK_MS = 400;

interface SwipeToDismissOptions {
    panelRef: RefObject<HTMLElement | null>;
    overlayRef: RefObject<HTMLElement | null>;
    onDismiss: () => void;
    reducedMotion: boolean;
}

export interface SwipeToDismissHandlers {
    onPointerDown: (event: PointerEvent<HTMLElement>) => void;
    onPointerMove: (event: PointerEvent<HTMLElement>) => void;
    onPointerUp: (event: PointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
    onClickCapture: (event: MouseEvent<HTMLElement>) => void;
}

interface Gesture {
    pointerId: number;
    startX: number;
    startY: number;
    decision: SwipeDecision;
    dx: number;
    width: number;
    samples: SwipeSample[];
}

/**
 * Drags a left-hand panel shut with a finger or a pen; a mouse never drags it.
 * The panel's `touch-action: pan-y` leaves vertical scrolling to the browser,
 * which cancels the pointer once it pans.
 */
export function useSwipeToDismiss({ panelRef, overlayRef, onDismiss, reducedMotion }: SwipeToDismissOptions): SwipeToDismissHandlers {
    const gesture = useRef<Gesture | null>(null);
    const closing = useRef(false);
    const swallowClick = useRef(false);
    const swallowTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const frame = useRef<number | null>(null);
    const cleanupClose = useRef<(() => void) | null>(null);
    const latestDismiss = useRef(onDismiss);
    const latestReducedMotion = useRef(reducedMotion);

    useEffect(() => {
        latestDismiss.current = onDismiss;
        latestReducedMotion.current = reducedMotion;
    });

    const stopSwallowing = useCallback(() => {
        swallowClick.current = false;
        if (swallowTimer.current !== null) clearTimeout(swallowTimer.current);
        swallowTimer.current = null;
    }, []);

    const cancelFrame = useCallback(() => {
        if (frame.current !== null) cancelAnimationFrame(frame.current);
        frame.current = null;
    }, []);

    useEffect(
        () => () => {
            cancelFrame();
            stopSwallowing();
            cleanupClose.current?.();
        },
        [cancelFrame, stopSwallowing],
    );

    const writeFollow = useCallback(() => {
        frame.current = null;
        const state = gesture.current;
        const panel = panelRef.current;
        if (!state || !panel) return;
        const dx = Math.min(0, state.dx);
        panel.style.transform = `translateX(${dx}px)`;
        const overlay = overlayRef.current;
        if (overlay && state.width > 0) overlay.style.opacity = String(Math.max(0, 1 + dx / state.width));
    }, [panelRef, overlayRef]);

    const springBack = useCallback(() => {
        cancelFrame();
        const panel = panelRef.current;
        const overlay = overlayRef.current;
        const snap = latestReducedMotion.current;
        if (panel) {
            panel.style.transition = snap ? "none" : `transform ${SWIPE.SPRING_MS}ms ease-out`;
            panel.style.transform = "";
        }
        if (overlay) {
            overlay.style.transition = snap ? "none" : `opacity ${SWIPE.SPRING_MS}ms ease-out`;
            overlay.style.opacity = "";
        }
    }, [panelRef, overlayRef, cancelFrame]);

    const close = useCallback(() => {
        cancelFrame();
        closing.current = true;
        const panel = panelRef.current;
        if (latestReducedMotion.current || !panel) {
            latestDismiss.current();
            return;
        }
        const overlay = overlayRef.current;
        panel.style.transition = `transform ${SWIPE.CLOSE_MS}ms ease-in`;
        panel.style.transform = "translateX(-100%)";
        if (overlay) {
            overlay.style.transition = `opacity ${SWIPE.CLOSE_MS}ms ease-in`;
            overlay.style.opacity = "0";
        }

        let done = false;
        const finish = () => {
            if (done) return;
            done = true;
            cleanupClose.current?.();
            latestDismiss.current();
        };
        const onEnd = (event: TransitionEvent) => {
            // A child's own transition bubbles here too.
            if (event.target !== panel) return;
            if (event.propertyName && event.propertyName !== "transform") return;
            finish();
        };
        const fallback = setTimeout(finish, CLOSE_FALLBACK_MS);
        panel.addEventListener("transitionend", onEnd);
        cleanupClose.current = () => {
            done = true;
            clearTimeout(fallback);
            panel.removeEventListener("transitionend", onEnd);
            cleanupClose.current = null;
        };
    }, [panelRef, overlayRef, cancelFrame]);

    const onPointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
        // A drag that ended without a click leaves no click to swallow.
        stopSwallowing();
        if (closing.current) return;
        if (event.pointerType !== "touch" && event.pointerType !== "pen") return;
        if (event.isPrimary === false) return;
        // Only one primary pointer exists at a time, so the one tracked has ended unheard.
        if (gesture.current?.decision === "claimed") springBack();
        gesture.current = null;
        if ((event.target as Element | null)?.closest?.(TEXT_ENTRY_SELECTOR)) return;
        gesture.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            decision: "pending",
            dx: 0,
            width: 0,
            samples: [{ t: event.timeStamp, x: event.clientX }],
        };
    }, [springBack, stopSwallowing]);

    const onPointerMove = useCallback(
        (event: PointerEvent<HTMLElement>) => {
            const state = gesture.current;
            if (!state || event.pointerId !== state.pointerId || state.decision === "rejected") return;
            const dx = event.clientX - state.startX;
            if (state.decision === "pending") {
                state.decision = decideSwipe(dx, event.clientY - state.startY);
                if (state.decision !== "claimed") return;
                event.currentTarget.setPointerCapture?.(event.pointerId);
                const panel = panelRef.current;
                if (panel) {
                    // Never cleared while mounted: clearing it would replay the slide-in.
                    panel.style.animation = "none";
                    panel.style.transition = "none";
                    state.width = panel.getBoundingClientRect().width;
                }
                const overlay = overlayRef.current;
                if (overlay) {
                    overlay.style.animation = "none";
                    overlay.style.transition = "none";
                }
            }
            state.dx = dx;
            state.samples.push({ t: event.timeStamp, x: event.clientX });
            const horizon = event.timeStamp - SWIPE.FLICK_WINDOW_MS;
            while (state.samples.length > 2 && state.samples[0].t < horizon) state.samples.shift();
            if (frame.current === null) frame.current = requestAnimationFrame(writeFollow);
        },
        [panelRef, overlayRef, writeFollow],
    );

    const onPointerUp = useCallback(
        (event: PointerEvent<HTMLElement>) => {
            const state = gesture.current;
            if (!state || event.pointerId !== state.pointerId) return;
            gesture.current = null;
            if (state.decision !== "claimed") return;
            stopSwallowing();
            swallowClick.current = true;
            swallowTimer.current = setTimeout(stopSwallowing, SWALLOW_CLICK_MS);
            const dx = event.clientX - state.startX;
            state.samples.push({ t: event.timeStamp, x: event.clientX });
            const velocity = velocityOf(state.samples, event.timeStamp);
            if (shouldDismiss({ dx, width: state.width, velocity })) close();
            else springBack();
        },
        [close, springBack, stopSwallowing],
    );

    const onPointerCancel = useCallback(
        (event: PointerEvent<HTMLElement>) => {
            const state = gesture.current;
            if (!state || event.pointerId !== state.pointerId) return;
            gesture.current = null;
            if (state.decision === "claimed") springBack();
        },
        [springBack],
    );

    const onClickCapture = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            // Enter, Space and assistive technology click with detail 0: only a pointer's click ends a drag.
            if (!swallowClick.current || event.detail === 0) return;
            stopSwallowing();
            event.preventDefault();
            event.stopPropagation();
        },
        [stopSwallowing],
    );

    return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClickCapture };
}
